"use client";

import { usePathname } from "next/navigation";

/**
 * ImpersonationBar — fixed amber strip at the top of the viewport shown
 * whenever a super admin is using "View As" mode.
 *
 * Shows the current impersonated role with a quick-switch menu and an
 * exit button.  Uses z-[9999] so it floats above everything including the
 * sticky header and side drawer.
 *
 * A matching ViewAsPicker (eye icon button) is shown to super admins even
 * when NOT impersonating, so they can enter View As mode from anywhere.
 */

import { useEffect, useState } from 'react';
import { useAuth, VIEW_AS_OPTIONS, type ViewAsValue } from '@/contexts/auth-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Eye, EyeOff, ChevronDown, X } from 'lucide-react';

/**
 * Tracks whether the chat window is open (broadcast by app-shell). The floating
 * "View As" button sits bottom-right — the same corner as the chat window's Send
 * button — so we hide it while chat is open to avoid covering Send.
 */
function useChatOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setOpen(document.body.dataset.chatOpen === 'true');
    }
    const onChange = (e: Event) => setOpen(!!(e as CustomEvent).detail?.open);
    window.addEventListener('bb-chat-open-change', onChange);
    return () => window.removeEventListener('bb-chat-open-change', onChange);
  }, []);
  return open;
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function getOption(value: string | null) {
  return VIEW_AS_OPTIONS.find(o => o.value === value) ?? null;
}

// ── ImpersonationBar ──────────────────────────────────────────────────────────

export function ImpersonationBar() {
  const { viewAs, setViewAs, realUserProfile } = useAuth();
  const chatOpen = useChatOpen();
  const pathname = usePathname();

  const isSuperAdmin =
    realUserProfile?.accountType?.toLowerCase() === 'superadmin';

  // Don't render anything if the real user is not a super admin — nor on the
  // event screens (/screens), which show only the films.
  if (!isSuperAdmin || pathname?.startsWith('/screens')) return null;

  const active = getOption(viewAs);

  // If not impersonating: show only the floating eye button (bottom-right).
  // Hide it while the chat window is open so it never covers the Send button.
  if (!active) {
    if (chatOpen) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            // Menu on the right (desktop): the pill moves to the left so it
            // never covers the menu's pinned Themes · Help · Account row.
            className="fixed bottom-24 right-4 z-[9999] flex items-center gap-1.5 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs font-medium px-3 py-2 shadow-xl backdrop-blur-sm transition-all menuright:sm:right-auto menuright:sm:left-4"
            title="View As — preview as a different account type"
          >
            <Eye className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">View As</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Preview as role
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {VIEW_AS_OPTIONS.map(opt => (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => setViewAs(opt.value as ViewAsValue)}
              className="gap-2 cursor-pointer"
            >
              <span>{opt.emoji}</span>
              <span>{opt.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Impersonating — show the amber banner
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-2 bg-amber-400 text-amber-950 px-3 py-1.5 text-xs font-semibold shadow-md">
      {/* Left: indicator */}
      <div className="flex items-center gap-1.5 min-w-0">
        <Eye className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">
          Viewing as{' '}
          <span className="font-bold">
            {active.emoji} {active.label}
          </span>
        </span>
      </div>

      {/* Centre: quick-switch */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-amber-950 hover:bg-amber-300 rounded gap-1"
          >
            Switch
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-48">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Switch view
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {VIEW_AS_OPTIONS.map(opt => (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => setViewAs(opt.value as ViewAsValue)}
              className={`gap-2 cursor-pointer ${opt.value === viewAs ? 'font-semibold' : ''}`}
            >
              <span>{opt.emoji}</span>
              <span>{opt.label}</span>
              {opt.value === viewAs && <span className="ml-auto text-primary">✓</span>}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Right: exit */}
      <button
        onClick={() => setViewAs(null)}
        className="flex items-center gap-1 rounded px-2 py-0.5 hover:bg-amber-300 transition-colors shrink-0"
        title="Exit View As mode"
      >
        <EyeOff className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Exit</span>
        <X className="h-3 w-3 sm:hidden" />
      </button>
    </div>
  );
}
